import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Dialog,
  DialogContent,
  Card,
  CardActionArea,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Fixation au sol pour `sol-mur` : produits double-hauteur / double-largeur disponibles.
const FLOOR_MOUNT_AVAILABLE_FOR_SOL_MUR = true;

const TypeForm = ({
  selectedType,
  onTypeChange,
  // 🆕 Etat WALLMOUNT (fixation murale vs au sol) — indépendant du type
  // d'installation. Le client peut choisir librement uniquement pour
  // `selectedType === 'sol-mur'`. Pour les autres types, le toggle est
  // affiché mais disabled (la valeur est imposée par le type).
  wallmount = true,
  onWallmountChange,
  // 🆕 Etat BOTTOM (face du dessous du cache). La checkbox n'est exposée
  // QUE pour `selectedType === 'mur-haut'` (cas où l'unité est en hauteur
  // et le dessous est visible). Pour les autres types, la checkbox est
  // masquée. BOTTOM est désactivé par défaut sur TOUS les types
  // (cf. ConfiguratorForm.handleTypeChange) ; l'activation est volontaire.
  bottom = false,
  onBottomChange,
  // REAR (face arrière) — exposée pour `sol` uniquement ; pas encore activable.
  rear = false,
  onRearChange,
}) => {
  // Les IDs ont été renommés le 25/05/2026 :
  //   'facade-haut' → 'mur-haut'
  //   'facade-sol'  → 'sol-mur'
  //   'sol'         → 'sol'   (masqué tant que la vignette dédiée n'est pas livrée)
  // Un 4e type 'mur' (façade proche du sol) est ajouté ; il réutilise l'asset
  // facade-haut.png en placeholder en attendant une illustration dédiée.
  // L'ordre de présentation est : mur-haut, mur, sol-mur (sol viendra ensuite).
  //
  // Libellés (révision du 26/05/2026) :
  //   mur-haut → "Sur façade en hauteur"           (cache haut sur console murale)
  //   mur      → "Sur façade proche du sol"        (cache mural en partie basse)
  //   sol-mur  → "Au sol, adossé au mur"           (cache appuyé au sol contre un mur)
  //   sol      → "Au sol, isolé"                   (caché tant que non livré)
  //
  // Images (révision du 26/05/2026) :
  //   - facade-haut.png : utilisée pour mur-haut.
  //   - facade-sol.png  : utilisée pour mur (en attendant une illustration "mur"
  //                       dédiée — c'est la plus proche visuellement d'un cache
  //                       mural en partie basse).
  //   - sol.png         : utilisée pour sol-mur (illustre les rubberfoots /
  //                       pieds de pose au sol, cohérent avec "Au sol, adossé
  //                       au mur").
  const installationTypes = [
    {
      id: "mur-haut",
      label: "Sur façade en hauteur",
      imgSrc: `${process.env.PUBLIC_URL}/types/facade-haut.png`,
      info: "Mon unité repose sur une console ou des équerres fixée(s) au mur, en hauteur",
    },
    {
      id: "mur",
      // TODO : remplacer imgSrc par une illustration "mur" dédiée quand elle sera dispo.
      label: "Sur façade proche du sol",
      imgSrc: `${process.env.PUBLIC_URL}/types/facade-sol.png`,
      info: "Mon unité repose sur une console ou des équerres fixée(s) au mur, proche du sol.",
    },
    {
      id: "sol-mur",
      label: "Au sol, adossé au mur",
      imgSrc: `${process.env.PUBLIC_URL}/types/sol.png`,
      info: "Mon unité est posée au sol (silentbloc ou similaire) et adossée contre un mur.",
    },
    {
      id: "sol",
      label: "Au sol, isolé",
      imgSrc: `${process.env.PUBLIC_URL}/types/sol-sans-cotes.png`,
      info: "Mon groupe extérieur repose sur des supports anti-vibration (silentbloc), sans mur.",
    },
  ];
  // Toggle modifiable uniquement pour `sol-mur`. Verrouillé (choix imposé) pour
  // mur-haut, mur et sol.
  const isWallmountToggleEditable =
    selectedType === "sol-mur" && FLOOR_MOUNT_AVAILABLE_FOR_SOL_MUR;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const [selectedInfo, setSelectedInfo] = useState(null);
  const popupRef = useRef(null);
  const carouselRef = useRef(null);
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (isXs && !selectedType) {
      // Premier type affiché (cf. installationTypes), aligné sur l'ordre voulu
      // mur-haut → mur → sol-mur.
      onTypeChange("mur-haut");
    }
  }, [isXs, selectedType, onTypeChange]);

  const handleInfoClick = (id) => {
    setSelectedInfo(selectedInfo === id ? null : id);
  };

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setSelectedInfo(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ===========================================================================
  // Sous-composant : toggle horizontal "fixation murale" / "fixation au sol"
  // ---------------------------------------------------------------------------
  // Switch MUI horizontal classique. Convention :
  //   - knob à GAUCHE (unchecked) → wallmount = true  (fixation murale)
  //   - knob à DROITE (checked)   → wallmount = false (fixation au sol)
  // Le label correspondant au côté actif s'affiche en gras + bleu de marque
  // (theme.palette.custom.blue, identique au titre "Préciser mon type
  // d'installation"). Quand disabled, on conserve les deux libellés mais
  // l'utilisateur ne peut pas interagir (type d'installation non ambigu).
  // ===========================================================================
  const WallmountToggle = ({ disabled }) => {
    const brandBlue = theme.palette.custom.blue;
    const tooltip = disabled
      ? "Choix imposé par le type d'installation."
      : "";
    const content = (
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1.5,
          mt: 3,
          mb: 2,
          px: 2.5,
          py: 1,
          border: "1px solid",
          borderColor: disabled ? "grey.300" : brandBlue,
          borderRadius: 999,
          backgroundColor: disabled ? "grey.50" : "background.paper",
          opacity: disabled ? 0.75 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: wallmount ? 700 : 400,
            color: wallmount ? brandBlue : "text.secondary",
            userSelect: "none",
          }}
        >
          Fixation murale
        </Typography>
        <Switch
          checked={!wallmount}
          disabled={disabled}
          onChange={(e) => {
            if (typeof onWallmountChange === "function") {
              // Switch coché (knob à droite) => fixation au sol => wallmount=false
              onWallmountChange(!e.target.checked);
            }
          }}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: brandBlue,
              "&:hover": { backgroundColor: `${brandBlue}14` },
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: brandBlue,
              opacity: 0.5,
            },
            "& .MuiSwitch-switchBase:not(.Mui-checked)": {
              color: brandBlue,
              "&:hover": { backgroundColor: `${brandBlue}14` },
            },
            "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
              backgroundColor: brandBlue,
              opacity: 0.5,
            },
          }}
          inputProps={{ "aria-label": "Fixation murale ou au sol" }}
        />
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-start",
            lineHeight: 1.1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: !wallmount ? 700 : 400,
              color: !wallmount ? brandBlue : "text.secondary",
              userSelect: "none",
            }}
          >
            Fixation au sol
          </Typography>
        </Box>
      </Box>
    );
    if (!tooltip) return content;
    return (
      <Tooltip title={tooltip} arrow placement="top">
        <span>{content}</span>
      </Tooltip>
    );
  };

  // ===========================================================================
  // Sous-composant : checkbox "J'ajoute la face du dessous"
  // ---------------------------------------------------------------------------
  // Affichée uniquement quand selectedType === 'mur-haut' (cf. usage plus bas).
  // Default = false (positionné par ConfiguratorForm.handleTypeChange pour
  // TOUS les types, y compris 'mur-haut'). L'activation est une action
  // volontaire du client : cocher pour ajouter le panneau du dessous, ce qui
  // déclenche un recalc des dimensions du cache (BOTTOM*30 dans la formule
  // maîtresse sizeZ) + ré-évaluation des règles d'affichage produit.
  // ===========================================================================
  const BottomFaceCheckbox = () => {
    const brandBlue = theme.palette.custom.blue;
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!bottom}
            onChange={(e) => {
              if (typeof onBottomChange === "function") {
                onBottomChange(e.target.checked);
              }
            }}
            sx={{
              color: brandBlue,
              "&.Mui-checked": { color: brandBlue },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              color: bottom ? brandBlue : "text.secondary",
              fontWeight: bottom ? 600 : 400,
              userSelect: "none",
            }}
          >
            J'ajoute la face du dessous
          </Typography>
        }
      />
    );
  };

  // Checkbox « J'ajoute la face arrière » — visible pour `sol`, pas encore activable.
  const RearFaceCheckbox = () => {
    const brandBlue = theme.palette.custom.blue;
    return (
      <FormControlLabel
        disabled
        control={
          <Checkbox
            checked={false}
            disabled
            sx={{
              color: brandBlue,
              "&.Mui-checked": { color: brandBlue },
            }}
          />
        }
        label={
          <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 400,
                userSelect: "none",
              }}
            >
              J'ajoute la face arrière
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "warning.dark",
                fontStyle: "italic",
                fontSize: "0.65rem",
                userSelect: "none",
              }}
            >
              Prochainement disponible
            </Typography>
          </Box>
        }
      />
    );
  };

  return (
    <Box sx={{ paddingRight: "20px", paddingLeft: "20px" }}>
      <Typography
        variant="h3"
        gutterBottom
        align="center"
        sx={{
          fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem", lg: "2rem" },
        }}
      >
        Préciser mon type d'installation
      </Typography>

      {isXs ? (
        <>
          <Box
            ref={carouselRef}
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              overflowX: "auto",
              justifyContent: "normal",
              gap: 2,
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE/Edge
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari
              },
            }}
          >
            {installationTypes.map((type, index) => (
              <Box
                key={type.id}
                ref={index === 0 ? firstItemRef : null}
                sx={{
                  width: 200,
                  position: "relative",
                  flex: "0 0 auto",
                }}
              >
                <CardActionArea
                  onClick={() => onTypeChange(type.id)}
                  sx={{ borderRadius: 2 }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      height: { xs: 180, sm: 200, md: 220 },
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border:
                        selectedType === type.id ? "2px solid #1976d2" : "none",
                    }}
                  >
                    <Box
                      component="img"
                      src={type.imgSrc}
                      alt={type.label}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Card>
                  <Box sx={{ textAlign: "center", mt: 1 }}>
                    {/* Mobile : on garde une mise en page sur 2 lignes
                        (1ère ligne = préfixe, 2e ligne = suffixe + InfoIcon).
                        Les libellés sont splités ici pour respecter ce layout :
                          mur-haut → "Sur façade" / "en hauteur"
                          mur      → "Sur façade" / "proche du sol"
                          sol-mur  → "Au sol,"    / "adossé au mur"
                          sol      → "Au sol,"    / "isolé" */}
                    {type.id === "mur-haut" || type.id === "mur" ? (
                      <>
                        <Typography variant="body2">Sur façade</Typography>
                        <Box
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <Typography variant="body2">
                            {type.id === "mur-haut" ? "en hauteur" : "proche du sol"}
                          </Typography>
                          <InfoIcon
                            sx={{
                              fontSize: "18px",
                              color: theme.palette.custom.blue,
                              ml: 1,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInfoClick(type.id);
                            }}
                          />
                        </Box>
                      </>
                    ) : type.id === "sol-mur" ? (
                      <>
                        <Typography variant="body2">Au sol,</Typography>
                        <Box
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <Typography variant="body2">adossé au mur</Typography>
                          <InfoIcon
                            sx={{
                              fontSize: "18px",
                              color: theme.palette.custom.blue,
                              ml: 1,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInfoClick(type.id);
                            }}
                          />
                        </Box>
                      </>
                    ) : type.id === "sol" ? (
                      <>
                        <Typography variant="body2">Au sol,</Typography>
                        <Box
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <Typography variant="body2">isolé</Typography>
                          <InfoIcon
                            sx={{
                              fontSize: "18px",
                              color: theme.palette.custom.blue,
                              ml: 1,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInfoClick(type.id);
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2">Sur des</Typography>
                        <Box
                          sx={{ display: "inline-flex", alignItems: "center" }}
                        >
                          <Typography variant="body2">supports au sol</Typography>
                          <InfoIcon
                            sx={{
                              fontSize: "18px",
                              color: theme.palette.custom.blue,
                              ml: 1,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInfoClick(type.id);
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                  {selectedInfo === type.id && (
                    <Dialog open={true} onClose={() => setSelectedInfo(null)}>
                      <DialogContent>
                        <Typography variant="body2" color="textSecondary">
                          {type.info}
                        </Typography>
                      </DialogContent>
                    </Dialog>
                  )}
                </CardActionArea>
              </Box>
            ))}
          </Box>

          {/* Checkbox "J'ajoute la face du dessous" — UNIQUEMENT pour
              'mur-haut'. Pour les autres types, BOTTOM est forcé silencieusement
              côté ConfiguratorForm.handleTypeChange. */}
          {selectedType === "mur-haut" && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <BottomFaceCheckbox />
            </Box>
          )}
          {selectedType === "sol" && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <RearFaceCheckbox />
            </Box>
          )}
          {selectedType && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <WallmountToggle disabled={!isWallmountToggleEditable} />
            </Box>
          )}
        </>
      ) : (
        <>
          <Grid container spacing={3} justifyContent="center">
            {installationTypes.map((type) => (
              <Grid
                key={type.id}
                item
                sx={{
                  width: { xs: 200, sm: 220, md: 250 },
                  position: "relative",
                }}
              >
                <CardActionArea
                  onClick={() => onTypeChange(type.id)}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <Card
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      height: { xs: 180, sm: 200, md: 220 },
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border:
                        selectedType === type.id ? "2px solid #1976d2" : "none",
                    }}
                  >
                    <Box
                      component="img"
                      src={type.imgSrc}
                      alt={type.label}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Card>
                  <Box
                    sx={{
                      textAlign: "center",
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ width: "90%" }}>{type.label}</Typography>
                    <InfoIcon
                      sx={{
                        fontSize: "18px",
                        color: theme.palette.custom.blue,
                        ml: 1,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInfoClick(type.id);
                      }}
                    />
                  </Box>
                </CardActionArea>
                {selectedInfo === type.id && (
                  <Box
                    ref={popupRef}
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: "57%",
                      transform: "translateX(-50%)",
                      mt: 1,
                      width: "200px",
                      p: 2,
                      zIndex: 1,
                      fontWeight: "300",
                      backgroundColor: "#f9f9f9",
                      boxShadow: "0 0 35px rgb(0 0 0 / 19%)",
                      borderRadius: "5px",
                      fontFamily:
                        "'Fira Sans',Helvetica,Arial,Lucida,sans-serif",
                      fontSize: "13px",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {type.info}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
          {/* Checkbox "J'ajoute la face du dessous" — UNIQUEMENT pour
              'mur-haut'. Pour les autres types, BOTTOM est forcé silencieusement
              côté ConfiguratorForm.handleTypeChange. */}
          {selectedType === "mur-haut" && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <BottomFaceCheckbox />
            </Box>
          )}
          {selectedType === "sol" && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <RearFaceCheckbox />
            </Box>
          )}
          {selectedType && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <WallmountToggle disabled={!isWallmountToggleEditable} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TypeForm;
